import React from "react";
import { AlertTriangle, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const RISKS = [
  {
    title: "Currency fluctuation",
    risk: "Between the day you pay and the day you complete, the local currency and yours can move — up or down.",
    mitigation: "All contracts are settled in a stable reference currency (GBP or USD) where possible. Payments already made are locked at that day's rate. Optional Quote Lock protects your headline price for up to 90 days.",
  },
  {
    title: "Project construction delays",
    risk: "Weather, permits, or supply chains can slow a build by weeks or months.",
    mitigation: "Milestone-based fund release means you never pre-pay for uncompleted stages. If a build stalls past the contract deadline, you may pause payments, request refund of unreleased escrow, or transfer to another MAVULA project.",
  },
  {
    title: "Changes in local property or tax law",
    risk: "Governments can change transfer duties, foreign ownership rules or inheritance tax rates.",
    mitigation: "Your contract is governed by the law in force at the date of signing. Independent local lawyers monitor changes and notify you before renewals or transfers.",
  },
  {
    title: "Force majeure",
    risk: "Floods, drought, civil unrest and pandemics can pause construction or reduce local rental demand.",
    mitigation: "Standard force-majeure clauses extend deadlines without penalty. Insurance covers physical loss of built structures once the roof is on.",
  },
  {
    title: "Secondary-market liquidity",
    risk: "Property is not as easy to sell as shares. Finding a buyer can take weeks or months.",
    mitigation: "MAVULA operates an optional buy-back at cost + earned interest (subject to your contract). You can also list on the internal MAVULA secondary marketplace or sell privately.",
  },
  {
    title: "Title / registration risk",
    risk: "In some countries, the property registry can be slow or contested.",
    mitigation: "Independent lawyers, named on your contract, complete the title-deed registration process. Optional third-party title insurance can be added at your request.",
  },
  {
    title: "Interest-rate risk on the balance",
    risk: "The rate set in your project contract is protected for the term stated there — but can only be renegotiated with your written agreement. Any illustrative rate figures shown elsewhere on this site are not a financial promotion and may not match your final contract.",
    mitigation: "You can pay down the balance early with no penalty. Rate resets are capped in the contract (never above the local mortgage market rate on the reset date).",
  },
  {
    title: "Counterparty risk (MAVULA itself)",
    risk: "What happens to my money if MAVULA the company ceases to trade?",
    mitigation: "Client money is held in a segregated account, not on MAVULA's balance sheet. If MAVULA ceased trading, the independent trustee continues to release funds to the builder or refunds you directly.",
  },
];

export default function RiskRealitiesPage() {
  return (
    <main data-testid="risk-realities-page">
      <section className="mv-green-bg text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Risk & Realities</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[0.98]">
            The honest list. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Every property scheme has one.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/85 text-[15.5px] leading-relaxed">
            Every serious investment carries risk. Anyone who tells you otherwise is selling something. Here is the plain-English list of what can go wrong — and exactly how we mitigate each one.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-14 md:py-20 space-y-4" data-testid="risk-list">
        {RISKS.map((r, i) => (
          <div key={r.title} className="mv-card p-6" data-testid={`risk-item-${i}`}>
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle size={18} className="mt-1" style={{ color: "#8a2020" }}/>
              <div className="font-display text-xl mv-green-text">{r.title}</div>
            </div>
            <div className="ml-8 space-y-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#8a2020" }}>What can go wrong</div>
                <p className="text-[14px] mt-1" style={{ color: "var(--mv-ink)" }}>{r.risk}</p>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#0A7A2C" }}>How we mitigate it</div>
                <p className="text-[14px] mt-1" style={{ color: "var(--mv-ink)" }}>{r.mitigation}</p>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="border-t mv-hairline py-14 md:py-20" style={{ background: "linear-gradient(180deg, #fff, rgba(10,58,36,0.06))" }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <ShieldCheck size={32} className="mx-auto mv-green-text"/>
          <h2 className="font-display text-3xl md:text-4xl mv-green-text mt-4">
            Read this. Take it to your lawyer.
          </h2>
          <p className="mt-4 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
            Nothing on this page is financial or legal advice. Before you commit, download the full Due-Diligence Pack and take independent advice in the country where the property is located.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/api/investor-diligence-pack" target="_blank" rel="noreferrer" className="btn-green" data-testid="risk-download-pack">
              Download Due-Diligence Pack <ArrowRight size={15}/>
            </a>
            <Link to="/how-your-money-is-held" className="btn-ghost" data-testid="risk-back-to-money">
              See how your money is held
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
